import React, { useState, useEffect } from 'react';
// `doc` is still needed by the invoice-status backfill below; `updateDoc`
// left with the role write it was the only caller of.
import { collection, getDocs, doc, writeBatch, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { useRole } from '../hooks/useRole';
import { useData } from '../context/DataContext';
import Sidebar, { SIDEBAR_W } from '../components/Sidebar';
import {
  BREAK_GLASS_EMAIL, ROLES, normalizeEmail, roleChangeBlockedReason, displayNameFromEmail,
} from '../data/teamMembers';
import { saveTeamMember, patchTeamMember, removeTeamMember } from '../utils/teamRolesDb';

// How many log entries to load. A single-field orderBy needs no composite
// index, and a cap keeps a log that grows for years from becoming the slowest
// thing on the page. The UI says when it is showing a capped view rather than
// letting the reader assume the list is complete.
const LOG_PAGE = 50;

// Entity types get their own label and colour, the same way roles do above, so
// a log of mixed deletions can be scanned by shape. An unknown type shows as
// itself rather than borrowing another's badge — the log is general, and the
// next caller must not need a change here to appear correctly.
const ENTITY_BADGE = {
  shopDrawing: { label: 'DRAWING', bg: '#E6F1FB', color: '#0077B6' },
  invoice: { label: 'INVOICE', bg: '#E6F7EE', color: '#0F6E56' },
  quote: { label: 'QUOTE', bg: '#FDF1DD', color: '#854F0B' },
  customer: { label: 'CUSTOMER', bg: '#F3E8FF', color: '#5B21B6' },
  product: { label: 'PRODUCT', bg: '#FFF3DC', color: '#7A4E00' },
};
const entityBadge = (t) =>
  ENTITY_BADGE[t] || { label: String(t || 'UNKNOWN').toUpperCase(), bg: '#F1F4F8', color: '#6B7C93' };

const fmtWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

// Every role the app can resolve, with its own label and its own colour.
//
// The badge used to be `role === 'admin' ? 'ADMIN' : 'SALESPERSON'`, which was
// fine while those were the only two. It is not fine now: 'salesperson' is not
// assigned to anyone, 'shop' is, and an either/or labelled the shop tablet
// SALESPERSON — a role that exists nowhere in the app. A map means the next
// role added shows up as itself rather than as whatever the else-branch says.
const ROLE_BADGE = {
  admin: { label: 'ADMIN', bg: '#E6F1FB', color: '#0077B6' },
  shop: { label: 'SHOP', bg: '#FFF3DC', color: '#7A4E00' },
  salesperson: { label: 'SALESPERSON', bg: '#CDE7F5', color: '#6B7C93' },
};
// An unrecognised value is shown as itself, uppercased, rather than silently
// borrowing another role's label.
const roleBadge = (role) =>
  ROLE_BADGE[role] || { label: String(role || 'UNKNOWN').toUpperCase(), bg: '#F1F4F8', color: '#6B7C93' };

function Settings() {
  const navigate = useNavigate();
  const {
    isAdmin, loading: roleLoading,
    userEmail, userName, teamRecords, teamReadFailed, refreshTeam,
  } = useRole();
  const {
    refreshProducts, refreshCustomers,
    productsLoadedAt, customersLoadedAt,
    loadingProducts, loadingCustomers,
  } = useData();
  // The EFFECTIVE team, from RoleContext: the Firestore `teamRoles` collection
  // merged over src/data/teamMembers.js — exactly what role resolution uses, so
  // this table can never show one thing while the app enforces another. (That
  // was the old trap with the `users` collection: a dropdown that wrote to a
  // document nothing read.)
  //
  // Already sorted by display name.
  const users = teamRecords;
  const [backfilling, setBackfilling] = useState(false);

  // ── Managing the team ──
  //
  // Every write goes through here, and EVERY GUARD IS RE-CHECKED IN THE HANDLER
  // rather than trusted to the JSX. The JSX decides what a person SEES; these
  // decide what the app WRITES, and a handler reached from a stale render — or
  // from anywhere else — must refuse just the same. (UI-level only: the repo
  // carries no Firestore rules, so this stops a mistake, not an attacker.)
  const [teamBusy, setTeamBusy] = useState('');
  const [teamError, setTeamError] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('admin');

  // Guard 1: nobody may remove or demote the LAST admin, and nobody may do it
  // to themselves. Both are one click from a lockout, and Settings is the only
  // screen where it could be undone.
  const blockedReason = (email, nextRole) =>
    roleChangeBlockedReason(teamRecords, email, nextRole, userEmail);

  // Guard 2: the break-glass account's credit-hold capability is not the UI's
  // to clear — it is unconditional in the resolver, so a control offering to
  // turn it off would be a lie as well as a trap.
  const isBreakGlassAccount = (email) => normalizeEmail(email) === BREAK_GLASS_EMAIL;

  const runTeamWrite = async (email, work) => {
    setTeamBusy(email);
    setTeamError('');
    try {
      await work();
      refreshTeam();     // re-read, so the table and the live role both update
    } catch (err) {
      console.error('Team write failed:', err);
      setTeamError(err?.message || 'Could not save. Check the connection and try again.');
    }
    setTeamBusy('');
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;
    const email = normalizeEmail(newEmail);
    if (!email) { setTeamError('Enter an email address.'); return; }
    if (!email.includes('@')) { setTeamError(`"${newEmail}" is not an email address.`); return; }
    if (!ROLES.includes(newRole)) { setTeamError('Pick a role.'); return; }
    await runTeamWrite(email, async () => {
      await saveTeamMember({ email, role: newRole, canManageCreditHold: false }, userName);
      setNewEmail('');
      setNewRole('admin');
    });
  };

  const handleChangeRole = async (member, role) => {
    if (!isAdmin || !ROLES.includes(role) || role === member.role) return;
    const blocked = blockedReason(member.email, role);
    if (blocked) { setTeamError(blocked); return; }
    await runTeamWrite(member.email, () =>
      saveTeamMember({ email: member.email, role, canManageCreditHold: member.canManageCreditHold }, userName));
  };

  const handleToggleCreditHold = async (member) => {
    if (!isAdmin) return;
    // The break-glass account keeps it unconditionally — refused here, not just
    // hidden in the JSX.
    if (isBreakGlassAccount(member.email)) return;
    const next = !member.canManageCreditHold;
    await runTeamWrite(member.email, async () => {
      // A file-only person has no record yet, so a patch would fail: write the
      // whole record, carrying their current role forward.
      if (member.source === 'file') {
        await saveTeamMember({ email: member.email, role: member.role, canManageCreditHold: next }, userName);
      } else {
        await patchTeamMember(member.email, { canManageCreditHold: next }, userName);
      }
    });
  };

  const handleRemoveMember = async (member) => {
    if (!isAdmin) return;
    const blocked = blockedReason(member.email, null);
    if (blocked) { setTeamError(blocked); return; }
    // A person in the FILE cannot be removed from here — only their Firestore
    // override can be. Said plainly rather than silently doing less than the
    // button appears to.
    const message = member.source === 'file'
      ? `${member.name} is in the code fallback, so removing them here does nothing until the file changes.`
      : member.source === 'both'
        ? `Remove ${member.name}'s Firestore record? They will fall back to their role in the code (${member.role}).`
        : `Remove ${member.name} (${member.email}) from the team?`;
    if (member.source === 'file') { setTeamError(message); return; }
    if (!window.confirm(message)) return;
    await runTeamWrite(member.email, () => removeTeamMember(member.email));
  };

  // ── Deletion log ──
  const [logEntries, setLogEntries] = useState([]);
  const [logLoading, setLogLoading] = useState(true);
  const [logError, setLogError] = useState(false);
  // Which entry's snapshot is open. One at a time: the snapshots are large and
  // two expanded at once turns the section into a wall of JSON.
  const [openEntry, setOpenEntry] = useState(null);

  const backfillInvoiceStatuses = async () => {
    if (!window.confirm(
      'This will set status="Picked Up" on every invoice that has no status field. ' +
      'Existing invoices with a status will not be changed. Continue?'
    )) return;
    setBackfilling(true);
    try {
      const snapshot = await getDocs(collection(db, 'invoices'));
      const batch = writeBatch(db);
      const now = new Date().toISOString();
      let count = 0;
      snapshot.docs.forEach(d => {
        if (!d.data().status) {
          batch.update(doc(db, 'invoices', d.id), {
            status: 'Picked Up',
            statusUpdatedAt: now,
          });
          count++;
        }
      });
      if (count > 0) {
        await batch.commit();
        alert(`✅ Updated ${count} invoice${count === 1 ? '' : 's'} with default status "Picked Up".`);
      } else {
        alert('All invoices already have a status field — nothing to update.');
      }
    } catch (err) {
      console.error('Backfill error:', err);
      alert('Error during backfill. Check console.');
    }
    setBackfilling(false);
  };

  // Newest first, capped. A single-field orderBy needs no composite index.
  const loadDeletionLog = async () => {
    setLogLoading(true);
    try {
      const snap = await getDocs(query(
        collection(db, 'deletionLog'),
        orderBy('deletedAt', 'desc'),
        limit(LOG_PAGE + 1),   // one extra, purely to detect that there ARE more
      ));
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setLogEntries(rows);
      setLogError(false);
    } catch (err) {
      console.error('Error loading the deletion log:', err);
      setLogError(true);
    }
    setLogLoading(false);
  };

  useEffect(() => {
    if (!roleLoading && isAdmin) loadDeletionLog();
    if (!roleLoading && !isAdmin) {
      navigate('/dashboard');
    }
  }, [roleLoading, isAdmin, navigate]);

  // loadUsers and updateRole are gone with the Firestore read and write. The
  // `users` collection itself is deliberately left alone — it is stale, not
  // harmful, and deleting data this app no longer relies on is not this
  // change's job.

  const formatAge = (ts) => {
    if (!ts) return 'Never loaded';
    const mins = Math.floor((Date.now() - ts) / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
    return `${Math.floor(hrs / 24)} day${Math.floor(hrs / 24) === 1 ? '' : 's'} ago`;
  };

  if (roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#CDE7F5' }}>
        <p style={{ color: '#6B7C93' }}>Loading...</p>
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <>
      <Sidebar />
      <div className="min-h-screen" style={{ marginLeft: SIDEBAR_W, background: '#CDE7F5' }}>

      <div className="p-6 max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-1">
          <h2 className="text-2xl font-bold" style={{ color: '#03045E' }}>Settings</h2>
          <span className="text-xs font-bold px-2 py-1 rounded" style={{ background: '#E6F1FB', color: '#0077B6' }}>ADMIN ONLY</span>
        </div>
        <p className="mb-6 text-sm" style={{ color: '#6B7C93' }}>Manage user roles and permissions for Atlantic Fence & Supply.</p>

        {/* Users Section */}
        <div className="rounded-xl p-6 mb-6" style={{ background: '#FFFFFF', border: '1px solid #E3ECF5' }}>
          <h3 className="font-bold text-lg mb-1" style={{ color: '#03045E' }}>Users</h3>
          <p className="text-sm mb-4" style={{ color: '#6B7C93' }}>Who can sign in, and what each of them can access.</p>

          <div className="rounded-lg px-4 py-3 mb-4 text-sm flex items-start gap-2" style={{ background: '#FDF1DD', border: '1px solid #E6C998', color: '#854F0B' }}>
            <span className="flex-shrink-0 mt-0.5">⚠️</span>
            <span>
              <strong>Adding someone here does not create their login.</strong> Create the account in
              <strong> Firebase Console → Authentication</strong> first; a person listed here with no
              login simply cannot sign in. And a person added only here falls back to the
              <strong> Shop</strong> role during a Firestore outage — for a long-term hire, also add
              them to <code className="px-1 rounded" style={{ background: '#FFFFFF', border: '1px solid #E6C998' }}>src/data/teamMembers.js</code> in a normal deploy.
            </span>
          </div>

          {teamReadFailed && (
            <div className="rounded-lg px-4 py-3 mb-4 text-sm" style={{ background: '#FBECEC', border: '1px solid #E9C3C3', color: '#A32D2D' }}>
              The team list could not be read from Firestore, so this is the code fallback only.
              Roles in force are still correct for these accounts; do not add anyone until this loads.
            </div>
          )}

          {teamError && (
            <div className="rounded-lg px-4 py-3 mb-4 text-sm" style={{ background: '#FBECEC', border: '1px solid #E9C3C3', color: '#A32D2D' }}>
              {teamError}
            </div>
          )}

          {/* Add a person. Admin only, like everything else on this screen. */}
          <form onSubmit={handleAddMember} className="flex flex-wrap items-end gap-2 mb-5">
            <div className="flex-1" style={{ minWidth: 220 }}>
              <label className="block text-xs font-semibold mb-1" style={{ color: '#6B7C93' }}>Email address</label>
              <input
                type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)}
                placeholder="name@atlanticfencesupply.com"
                className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none"
                style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: '#6B7C93' }}>Role</label>
              <select
                value={newRole} onChange={e => setNewRole(e.target.value)}
                className="rounded-lg px-3 py-2 text-sm focus:outline-none"
                style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
              >
                {ROLES.map(r => <option key={r} value={r}>{roleBadge(r).label}</option>)}
              </select>
            </div>
            <button
              type="submit" disabled={!!teamBusy}
              className="font-bold px-4 py-2 rounded-lg text-sm disabled:opacity-60"
              style={{ background: '#0077B6', color: '#FFFFFF' }}
            >
              {teamBusy ? 'Saving…' : '+ Add Person'}
            </button>
            {newEmail.trim() && (
              <span className="text-xs" style={{ color: '#6B7C93' }}>
                Will be known as <strong style={{ color: '#03045E' }}>{displayNameFromEmail(newEmail)}</strong>
              </span>
            )}
          </form>

          {(
            <div className="overflow-hidden rounded-xl" style={{ border: '1px solid #E3ECF5' }}>
              <table className="w-full text-sm">
                <thead style={{ background: '#E6F1FB', borderBottom: '1px solid #E3ECF5' }}>
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold" style={{ color: '#6B7C93' }}>Name</th>
                    <th className="px-4 py-3 text-left font-semibold" style={{ color: '#6B7C93' }}>Email</th>
                    <th className="px-4 py-3 text-left font-semibold" style={{ color: '#6B7C93' }}>Role</th>
                    <th className="px-4 py-3 text-left font-semibold" style={{ color: '#6B7C93' }}>Credit hold</th>
                    <th className="px-4 py-3 text-left font-semibold" style={{ color: '#6B7C93' }}>Source</th>
                    <th className="px-4 py-3" aria-label="Remove" />
                  </tr>
                </thead>
                <tbody>
                  {/* The display name is DERIVED from the email, by the same
                      rule RoleContext uses for the live session — so what shows
                      here is what the app will call that person. It is never
                      stored, here or in Firestore. */}
                  {users.map((u, i) => {
                    const breakGlass = isBreakGlassAccount(u.email);
                    const roleBlocked = blockedReason(u.email, 'shop');
                    const removeBlocked = blockedReason(u.email, null);
                    const busy = teamBusy === u.email;
                    return (
                    <tr key={u.email} style={{ background: i % 2 === 0 ? '#FFFFFF' : '#CDE7F5', borderTop: '1px solid #E3ECF5' }}>
                      <td className="px-4 py-3 font-bold" style={{ color: '#03045E' }}>
                        {u.name}
                        {normalizeEmail(u.email) === normalizeEmail(userEmail) && (
                          <span className="ml-2 text-xs font-semibold" style={{ color: '#6B7C93' }}>(you)</span>
                        )}
                      </td>
                      <td className="px-4 py-3" style={{ color: '#6B7C93' }}>{u.email}</td>
                      <td className="px-4 py-3">
                        {/* Disabled rather than hidden when a change would lock
                            someone out — with the reason in the tooltip, so the
                            rule is visible instead of mysterious. The handler
                            refuses independently. */}
                        <select
                          value={u.role}
                          disabled={busy || !!roleBlocked}
                          title={roleBlocked || `Change ${u.name}'s role`}
                          onChange={e => handleChangeRole(u, e.target.value)}
                          className="rounded px-2 py-1 text-xs font-bold disabled:opacity-60"
                          style={{ background: roleBadge(u.role).bg, color: roleBadge(u.role).color, border: '1px solid #E3ECF5' }}
                        >
                          {ROLES.map(r => <option key={r} value={r} style={{ background: '#FFFFFF', color: '#03045E' }}>{roleBadge(r).label}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        {breakGlass ? (
                          <span className="text-xs font-bold" title="The owner's account always carries this — it is the way back in if anything else goes wrong." style={{ color: '#0F6E56' }}>
                            ✓ Always
                          </span>
                        ) : (
                          <label className="flex items-center gap-2 text-xs" style={{ color: '#6B7C93' }}>
                            <input
                              type="checkbox"
                              checked={!!u.canManageCreditHold}
                              disabled={busy}
                              onChange={() => handleToggleCreditHold(u)}
                              className="w-4 h-4"
                              style={{ accentColor: '#0077B6' }}
                            />
                            {u.canManageCreditHold ? 'Can place / lift' : 'No'}
                          </label>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs" style={{ color: '#6B7C93' }}>
                        {u.source === 'db' ? 'Settings' : u.source === 'both' ? 'Settings (overrides code)' : 'Code fallback'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleRemoveMember(u)}
                          disabled={busy || !!removeBlocked || u.source === 'file'}
                          title={removeBlocked || (u.source === 'file'
                            ? 'In the code fallback — remove them from src/data/teamMembers.js and deploy'
                            : `Remove ${u.name}`)}
                          className="text-xs font-bold px-2 py-1 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                          style={{ background: 'transparent', color: '#A32D2D', border: '1px solid #E9C3C3' }}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Refresh Data */}
        <div className="rounded-xl p-6 mb-6" style={{ background: '#FFFFFF', border: '1px solid #E3ECF5' }}>
          <h3 className="font-bold text-lg mb-1" style={{ color: '#03045E' }}>Refresh Cached Data</h3>
          <p className="text-sm mb-4" style={{ color: '#6B7C93' }}>
            Products and customers are cached locally for 1 hour to reduce Firestore reads. Use these buttons to force a fresh sync.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl p-4" style={{ border: '1px solid #E3ECF5' }}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-semibold text-sm" style={{ color: '#03045E' }}>Product List</p>
                  <p className="text-xs" style={{ color: '#6B7C93' }}>Last updated: {formatAge(productsLoadedAt)}</p>
                </div>
                <button
                  onClick={refreshProducts}
                  disabled={loadingProducts}
                  className="disabled:opacity-50 font-bold px-4 py-2 rounded-lg text-sm transition"
                  style={{ background: '#0077B6', border: 'none', color: '#fff', cursor: 'pointer' }}
                >
                  {loadingProducts ? 'Refreshing...' : 'Refresh Products'}
                </button>
              </div>
            </div>
            <div className="rounded-xl p-4" style={{ border: '1px solid #E3ECF5' }}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-semibold text-sm" style={{ color: '#03045E' }}>Customer List</p>
                  <p className="text-xs" style={{ color: '#6B7C93' }}>Last updated: {formatAge(customersLoadedAt)}</p>
                </div>
                <button
                  onClick={refreshCustomers}
                  disabled={loadingCustomers}
                  className="disabled:opacity-50 font-bold px-4 py-2 rounded-lg text-sm transition"
                  style={{ background: '#0077B6', border: 'none', color: '#fff', cursor: 'pointer' }}
                >
                  {loadingCustomers ? 'Refreshing...' : 'Refresh Customers'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Data Migrations */}
        <div className="rounded-xl p-6 mb-6" style={{ background: '#FFFFFF', border: '1px solid #E3ECF5' }}>
          <h3 className="font-bold text-lg mb-1" style={{ color: '#03045E' }}>Data Migrations</h3>
          <p className="text-sm mb-4" style={{ color: '#6B7C93' }}>
            One-time fixes for historical data. Safe to run multiple times — they only touch records that need updating.
          </p>
          <div className="rounded-xl p-4" style={{ border: '1px solid #E3ECF5' }}>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-semibold text-sm" style={{ color: '#03045E' }}>Initialize Invoice Statuses</p>
                <p className="text-xs" style={{ color: '#6B7C93' }}>
                  Sets <code className="px-1 rounded" style={{ background: '#CDE7F5', border: '1px solid #E3ECF5' }}>status: "Picked Up"</code> on any invoice that has no status field, so old invoices don't clutter the active board.
                </p>
              </div>
              <button
                onClick={backfillInvoiceStatuses}
                disabled={backfilling}
                className="disabled:opacity-50 font-bold px-4 py-2 rounded-lg text-sm transition whitespace-nowrap"
                style={{ background: '#FDF1DD', border: '1px solid #E6C998', color: '#854F0B', cursor: 'pointer' }}
              >
                {backfilling ? 'Updating...' : '🔄 Initialize Invoice Statuses'}
              </button>
            </div>
          </div>
        </div>

        {/* ── DELETION LOG ─────────────────────────────────────────────
            What has been deleted, by whom, and when — every entity type, not
            just drawings.

            ⚠️ READ ONLY, AND DELIBERATELY SO. There is no edit control and no
            delete control anywhere in this section, because a log that can be
            erased by the person who needs erasing is not a log. Note this is a
            UI-LEVEL protection only: Firestore rules currently allow any
            authenticated user to read and write anything, so a determined
            person could alter or remove entries directly. That closes with the
            rules step; until then this stops a colleague, not an adversary.

            Admin only — the whole Settings route already redirects anyone else,
            and this section renders inside that guard. */}
        <div className="rounded-xl p-6 mb-6" style={{ background: '#FFFFFF', border: '1px solid #E3ECF5' }}>
          <div className="flex items-center justify-between gap-4 flex-wrap mb-1">
            <h3 className="font-bold text-lg" style={{ color: '#03045E' }}>Deletion Log</h3>
            <button
              onClick={loadDeletionLog}
              disabled={logLoading}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg"
              style={{
                background: '#E6F1FB', border: '1px solid #E3ECF5', color: '#0077B6',
                cursor: logLoading ? 'default' : 'pointer', opacity: logLoading ? 0.5 : 1,
              }}
            >
              {logLoading ? 'Loading…' : '🔄 Refresh'}
            </button>
          </div>
          <p className="text-sm mb-4" style={{ color: '#6B7C93' }}>
            A permanent record of deleted records. Read-only — entries cannot be edited or removed here.
          </p>

          {logLoading ? (
            <p className="text-sm" style={{ color: '#6B7C93' }}>Loading…</p>
          ) : logError ? (
            <p className="text-sm" style={{ color: '#A32D2D' }}>
              Could not load the deletion log. Check the connection and press Refresh.
            </p>
          ) : logEntries.length === 0 ? (
            <p className="text-sm" style={{ color: '#6B7C93' }}>Nothing has been deleted.</p>
          ) : (
            <>
              {/* One extra row was requested purely to detect that more exist;
                  it is never shown, so the reader is never told the list is
                  complete when it is not. */}
              {logEntries.length > LOG_PAGE && (
                <p className="text-xs mb-3" style={{ color: '#854F0B' }}>
                  Showing the {LOG_PAGE} most recent deletions. There are more.
                </p>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {logEntries.slice(0, LOG_PAGE).map(entry => {
                  const badge = entityBadge(entry.entityType);
                  const open = openEntry === entry.id;
                  return (
                    <div key={entry.id} className="rounded-lg" style={{ border: '1px solid #E3ECF5', background: '#FAFCFE' }}>
                      {/* The row is a BUTTON because its only action is to
                          expand — there is nothing else to click, and nothing
                          here changes a record. */}
                      <button
                        onClick={() => setOpenEntry(open ? null : entry.id)}
                        aria-expanded={open}
                        className="w-full text-left px-4 py-3"
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                      >
                        <div className="flex items-center gap-3 flex-wrap">
                          <span
                            className="text-xs font-bold px-2 py-1 rounded"
                            style={{ background: badge.bg, color: badge.color }}
                          >
                            {badge.label}
                          </span>
                          <span className="font-bold" style={{ color: '#03045E', fontFamily: 'monospace', fontSize: 13 }}>
                            {entry.label || '(no label)'}
                          </span>
                          {entry.detail && (
                            <span className="text-sm" style={{ color: '#03045E' }}>{entry.detail}</span>
                          )}
                          <span className="text-xs ml-auto" style={{ color: '#6B7C93' }}>
                            {entry.deletedBy || '—'} · {fmtWhen(entry.deletedAt)}
                          </span>
                          <span className="text-xs" style={{ color: '#9AA8BC' }}>{open ? '▲' : '▼'}</span>
                        </div>
                      </button>

                      {open && (
                        <div className="px-4 pb-3" style={{ borderTop: '1px solid #E3ECF5' }}>
                          {entry.snapshotTruncated && (
                            <p className="text-xs mt-2 mb-1" style={{ color: '#854F0B' }}>
                              ⚠️ This snapshot was too large to store in full.
                              {Array.isArray(entry.snapshotOmittedFields) && entry.snapshotOmittedFields.length > 0
                                ? ` Omitted: ${entry.snapshotOmittedFields.join(', ')}.`
                                : ''}
                            </p>
                          )}
                          <p className="text-xs mt-2 mb-1 font-semibold" style={{ color: '#6B7C93' }}>
                            What was deleted (document {entry.entityId || '—'}):
                          </p>
                          {/* Plain JSON. The snapshot is an arbitrary shape from
                              an arbitrary collection, and inventing a per-type
                              renderer would mean touching this file every time a
                              new caller appears — the exact coupling the log was
                              built to avoid. */}
                          <pre
                            className="text-xs rounded-lg p-3"
                            style={{
                              background: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E',
                              maxHeight: 260, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                              margin: 0, fontFamily: 'monospace',
                            }}
                          >
                            {JSON.stringify(entry.snapshot ?? {}, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Role Legend */}
        <div className="rounded-xl p-6" style={{ background: '#FFFFFF', border: '1px solid #E3ECF5' }}>
          <h3 className="font-bold text-lg mb-4" style={{ color: '#03045E' }}>Role Permissions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl p-4" style={{ background: '#E6F1FB', border: '1px solid #E3ECF5' }}>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-bold px-2 py-1 rounded" style={{ background: '#0077B6', color: '#fff' }}>ADMIN</span>
              </div>
              <ul className="text-sm space-y-1" style={{ color: '#03045E' }}>
                <li>✓ View and edit all quotes & invoices</li>
                <li>✓ Edit cost prices, markups, and product descriptions</li>
                <li>✓ Import products via CSV</li>
                <li>✓ View sales charts and reports</li>
                <li>✓ Access Settings and manage user roles</li>
                <li>✓ Delete documents</li>
                <li>✓ Filter documents by salesperson</li>
              </ul>
            </div>
            {/* The workshop tablet. Described here because it is a role somebody
                actually holds — the salesperson role is defined in the code but
                is not currently assigned to anyone. */}
            <div className="rounded-xl p-4" style={{ background: '#FFF7E8', border: '1px solid #E3ECF5' }}>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-bold px-2 py-1 rounded" style={{ background: '#7A4E00', color: '#fff' }}>SHOP</span>
              </div>
              <ul className="text-sm space-y-1" style={{ color: '#6B7C93' }}>
                <li>✓ See every gate drawing and mark it Pending / In Progress / Complete</li>
                <li>✓ See invoices the office has sent to be picked, and mark them Ready</li>
                <li>✓ Read and print a drawing sheet or a picking list</li>
                <li style={{ color: '#A32D2D' }}>✗ Cannot reach any other screen — every route returns to the shop screen</li>
                <li style={{ color: '#A32D2D' }}>✗ Cannot see prices, costs or totals anywhere</li>
                <li style={{ color: '#A32D2D' }}>✗ Cannot create, edit or delete anything</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
}

export default Settings;
